import {
    ArrowDownTrayIcon,
    CalendarDaysIcon,
    ChatBubbleLeftRightIcon,
    CheckCircleIcon,
    ClockIcon,
    CurrencyRupeeIcon,
    DocumentArrowDownIcon,
    DocumentIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    MagnifyingGlassIcon,
    PhotoIcon,
    PlusIcon,
    ReceiptPercentIcon,
    TagIcon,
    TrashIcon,
    UserCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import RecordPaymentDialog from './RecordPaymentDialog';
import { downloadCsv, formatDateForCsv } from '../lib/csvExport';
import { fetchJsonOrThrow, getApiErrorMessage } from '../lib/apiError';
import { cn } from '../lib/utils';
import StudentFeeRecords from './StudentFeeRecords';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Function to get token from localStorage
const getToken = () => {
  return localStorage.getItem('admin_token');
};

// ===== SKELETON LOADERS =====

// Bubbly expanding card skeleton
const CardSkeleton = ({ color = 'green' }) => {
  const colorClasses = {
    green: 'bg-green-100',
    yellow: 'bg-yellow-100',
    red: 'bg-red-100',
    blue: 'bg-blue-100'
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 space-y-3">
            <div className={`h-4 ${colorClasses[color]} rounded-full w-24 animate-pulse-scale`}></div>
            <div className={`h-8 ${colorClasses[color]} rounded-full w-32 animate-pulse-scale-delay`}></div>
          </div>
          <div className={`p-4 ${colorClasses[color]} rounded-xl animate-bubble-bounce`}>
            <div className="h-6 w-6 bg-white/50 rounded-full animate-spin-slow"></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Payment card skeleton with wave effect
const PaymentCardSkeleton = ({ index = 0 }) => {
  return (
    <div 
      className="flex items-center justify-between p-3 border rounded-lg overflow-hidden relative animate-fade-in-up"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
      <div className="flex items-center space-x-3 min-w-0 flex-1 z-10">
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center animate-bubble-pulse">
          <div className="w-6 h-6 bg-green-300 rounded-full animate-ping-slow"></div>
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded-full w-32 animate-pulse"></div>
          <div className="h-3 bg-gray-100 rounded-full w-40 animate-pulse-delay"></div>
        </div>
      </div>
      <div className="text-right flex-shrink-0 ml-2 space-y-2 z-10">
        <div className="h-5 bg-green-100 rounded-full w-20 animate-pulse"></div>
        <div className="h-3 bg-gray-100 rounded-full w-16 animate-pulse-delay"></div>
      </div>
    </div>
  );
};

// Due date card skeleton with alert animation
const DueDateCardSkeleton = ({ index = 0 }) => {
  return (
    <div 
      className="flex items-center justify-between p-3 border rounded-lg overflow-hidden relative animate-fade-in-up"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-50/40 to-transparent animate-shimmer-slow"></div>
      <div className="flex items-center space-x-3 min-w-0 flex-1 z-10">
        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center animate-bubble-pulse-delay">
          <div className="w-6 h-6 bg-yellow-300 rounded-full animate-ping-slower"></div>
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded-full w-32 animate-pulse"></div>
          <div className="h-3 bg-gray-100 rounded-full w-40 animate-pulse-delay"></div>
        </div>
      </div>
      <div className="text-right flex-shrink-0 ml-2 space-y-2 z-10">
        <div className="h-5 bg-yellow-100 rounded-full w-20 animate-pulse"></div>
        <div className="h-3 bg-yellow-100 rounded-full w-16 animate-pulse-delay"></div>
        <div className="h-6 bg-blue-100 rounded-full w-16 mt-2 animate-pulse"></div>
      </div>
    </div>
  );
};

// Table row skeleton with staggered animation
const TableRowSkeleton = ({ columns = 7, index = 0 }) => {
  return (
    <TableRow 
      className="animate-fade-in"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <TableCell key={i}>
          <div 
            className={`h-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] rounded-full animate-shimmer-fast`}
            style={{ 
              width: i === 0 ? '120px' : i === columns - 1 ? '80px' : '100px',
              animationDelay: `${i * 100}ms`
            }}
          ></div>
        </TableCell>
      ))}
    </TableRow>
  );
};

// Overview section skeleton
const OverviewSkeleton = () => {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <CardSkeleton color="green" />
        <CardSkeleton color="yellow" />
        <CardSkeleton color="red" />
        <CardSkeleton color="blue" />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recently Paid Skeleton */}
        <Card className="animate-fade-in-up">
          <CardHeader className="pb-3">
            <div className="h-6 bg-gray-200 rounded-full w-40 animate-pulse mb-2"></div>
            <div className="h-4 bg-gray-100 rounded-full w-48 animate-pulse-delay"></div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[0, 1, 2, 3, 4].map((index) => (
                <PaymentCardSkeleton key={index} index={index} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Due Skeleton */}
        <Card className="animate-fade-in-up" style={{ animationDelay: '200ms' }}>
          <CardHeader className="pb-3">
            <div className="h-6 bg-gray-200 rounded-full w-40 animate-pulse mb-2"></div>
            <div className="h-4 bg-gray-100 rounded-full w-48 animate-pulse-delay"></div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[0, 1, 2, 3, 4].map((index) => (
                <DueDateCardSkeleton key={index} index={index} />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Table skeleton
const TableSkeleton = ({ rows = 5, columns = 7 }) => {
  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <div className="h-6 bg-gray-200 rounded-full w-40 animate-pulse mb-2"></div>
        <div className="h-4 bg-gray-100 rounded-full w-56 animate-pulse-delay"></div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {Array.from({ length: columns }).map((_, i) => (
                  <TableHead key={i}>
                    <div className="h-4 bg-gray-300 rounded-full w-20 animate-pulse"></div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: rows }).map((_, i) => (
                <TableRowSkeleton key={i} columns={columns} index={i} />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

// Dialog content skeleton
const DialogContentSkeleton = () => {
  return (
    <div className="space-y-4 p-4 animate-fade-in">
      <div className="flex justify-center">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 bg-blue-200 rounded-full animate-ping-slow"></div>
          <div className="absolute inset-0 bg-blue-300 rounded-full animate-bubble-pulse"></div>
          <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
      <div className="text-center space-y-2">
        <div className="h-5 bg-gray-200 rounded-full w-48 mx-auto animate-pulse"></div>
        <div className="h-4 bg-gray-100 rounded-full w-64 mx-auto animate-pulse-delay"></div>
      </div>
    </div>
  );
};

// ===== ADD CUSTOM ANIMATIONS TO YOUR GLOBAL CSS =====
// Add these keyframes to your global CSS or Tailwind config
const styles = `
@keyframes pulse-scale {
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.05); opacity: 1; }
}

@keyframes pulse-scale-delay {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.08); opacity: 1; }
}

@keyframes bubble-bounce {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-8px) scale(1.1); }
}

@keyframes bubble-pulse {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
}

@keyframes bubble-pulse-delay {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.2); opacity: 1; }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes shimmer-slow {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes shimmer-fast {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes ping-slow {
  0% { transform: scale(1); opacity: 1; }
  75%, 100% { transform: scale(1.5); opacity: 0; }
}

@keyframes ping-slower {
  0% { transform: scale(1); opacity: 1; }
  75%, 100% { transform: scale(1.8); opacity: 0; }
}

@keyframes pulse-delay {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

.animate-pulse-scale {
  animation: pulse-scale 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-pulse-scale-delay {
  animation: pulse-scale-delay 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  animation-delay: 0.3s;
}

.animate-bubble-bounce {
  animation: bubble-bounce 2s ease-in-out infinite;
}

.animate-bubble-pulse {
  animation: bubble-pulse 2s ease-in-out infinite;
}

.animate-bubble-pulse-delay {
  animation: bubble-pulse-delay 2s ease-in-out infinite;
  animation-delay: 0.5s;
}

.animate-shimmer {
  animation: shimmer 2s ease-in-out infinite;
}

.animate-shimmer-slow {
  animation: shimmer-slow 3s ease-in-out infinite;
}

.animate-shimmer-fast {
  animation: shimmer-fast 1.5s linear infinite;
}

.animate-fade-in-up {
  animation: fade-in-up 0.6s ease-out forwards;
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out forwards;
}

.animate-spin-slow {
  animation: spin-slow 3s linear infinite;
}

.animate-ping-slow {
  animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.animate-ping-slower {
  animation: ping-slower 3s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.animate-pulse-delay {
  animation: pulse-delay 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  animation-delay: 0.2s;
}
`;

// File Upload Component
const FileUpload = ({
  onFileUpload,
  onFileRemove,
  currentFileUrl = '',
  accept = 'image/*,application/pdf',
  maxSize = 10 * 1024 * 1024,
  className = ''
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const acceptedTypes = accept.split(',');
    const isTypeValid = acceptedTypes.some(type => {
      if (type === 'image/*') return file.type.startsWith('image/');
      if (type === 'application/pdf') return file.type === 'application/pdf';
      return file.type === type;
    });

    if (!isTypeValid) {
      toast.error('Please select a valid file type (PNG, JPG, PDF)');
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      toast.error(`File size should be less than ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview('');
    }

    // Auto-upload file
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/storage/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Upload failed');

      const result = await response.json();

      if (onFileUpload) {
        onFileUpload(result.url);
      }

      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
      setSelectedFile(null);
      setPreview('');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (currentFileUrl) {
      try {
        const token = getToken();
        await fetch(`${API_BASE_URL}/storage/delete`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uri: currentFileUrl }),
        });

        if (onFileRemove) {
          onFileRemove();
        }

        toast.success('File removed successfully');
      } catch (error) {
        console.error('Delete error:', error);
        toast.error('Failed to remove file');
      }
    }
    setSelectedFile(null);
    setPreview('');
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreview('');
  };

  const displayUrl = currentFileUrl || (selectedFile ? 'Uploading...' : '');

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Preview Section */}
      {(preview || currentFileUrl) && (
        <div className="flex flex-col items-center space-y-2 p-4 border rounded-lg bg-gray-50 animate-fade-in-up">
          {uploading ? (
            <div className="w-32 h-32 flex items-center justify-center">
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 bg-blue-200 rounded-full animate-ping-slow"></div>
                <div className="absolute inset-0 bg-blue-300 rounded-full animate-bubble-pulse"></div>
              </div>
            </div>
          ) : preview ? (
            <img
              src={preview}
              alt="File preview"
              className="w-32 h-32 object-cover rounded-lg border border-gray-200 animate-fade-in"
            />
          ) : currentFileUrl ? (
            <div className="w-32 h-32 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 animate-fade-in">
              <DocumentIcon className="h-12 w-12 text-gray-400" />
            </div>
          ) : null}

          {displayUrl && (
            <p className="text-xs text-gray-500 text-center break-all max-w-xs">
              {displayUrl}
            </p>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={currentFileUrl ? handleRemoveFile : clearSelection}
              className="text-red-600"
              disabled={uploading}
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              {currentFileUrl ? 'Delete File' : 'Remove'}
            </Button>
          </div>
        </div>
      )}

      {/* File Input */}
      {!preview && !currentFileUrl && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
          <PhotoIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">
            {uploading ? 'Uploading...' : 'Upload file'}
          </p>
          <Input
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
            id="file-upload"
            disabled={uploading}
          />
          <Label
            htmlFor="file-upload"
            className={`cursor-pointer px-4 py-2 rounded-md text-sm font-medium transition-colors ${uploading
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
          >
            {uploading ? 'Uploading...' : 'Choose File'}
          </Label>
          <p className="text-xs text-gray-500 mt-2">
            PNG, JPG, PDF up to {maxSize / (1024 * 1024)}MB
          </p>
        </div>
      )}
    </div>
  );
};

// API service functions for Fee Management
const feeApiService = {
  // Get fee invoices for a student
  async getStudentInvoices(studentId) {
    // console.log(`Fetching invoices for student: ${studentId}`);
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fees/invoices/${studentId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    // console.log('Invoices response status:', response.status);
    if (!response.ok) throw new Error('Failed to fetch invoices');
    const data = await response.json();
    // console.log('Invoices data:', data);
    return data;
  },

  // Get payment history for a student
  async getPaymentHistory(studentId, page = 1) {
    // console.log(`Fetching payment history for student: ${studentId}`);
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fees/history/${studentId}?page=${page}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    // console.log('Payment history response status:', response.status);
    if (!response.ok) throw new Error('Failed to fetch payment history');
    const data = await response.json();
    // console.log('Payment history data:', data);
    return data;
  },

  // Record a payment
  async recordPayment(paymentData) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fees/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    });
    if (!response.ok) throw new Error('Failed to record payment');
    return response.json();
  },

  // Send fee reminder
  async sendFeeReminder(reminderData) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/manual-reminders/fee-reminder`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reminderData),
    });
    if (!response.ok) throw new Error('Failed to send reminder');
    return response.json();
  },

  // Get recently paid students
  async getRecentlyPaid() {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fees/recent-payments`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch recent payments');
    return response.json();
  },

  // Get upcoming due dates
  async getUpcomingDueDates() {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fees/upcoming-due`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch upcoming due dates');
    return response.json();
  },

  // Preview fee update for an invoice
  async previewFeeUpdate(invoiceId, newAmount) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fee-adjustments/invoices/${invoiceId}/preview-fee-update`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ newAmount }),
    });
    return fetchJsonOrThrow(response, 'Failed to preview fee update');
  },

  // Apply fee update for an invoice
  async updateFeeAmount(invoiceId, newAmount, reason) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fee-adjustments/invoices/${invoiceId}/update-fee`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ newAmount, reason }),
    });
    return fetchJsonOrThrow(response, 'Failed to update fee amount');
  },

  // Apply additional discount (works after payments too)
  async applyAdditionalDiscount(invoiceId, discountAmount, reason) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fee-adjustments/invoices/${invoiceId}/apply-additional-discount`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ discountAmount, reason }),
    });
    return fetchJsonOrThrow(response, 'Failed to apply discount');
  },

  // Get discount history for an invoice
  async getDiscountHistory(invoiceId) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fee-adjustments/invoices/${invoiceId}/discount-history`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return fetchJsonOrThrow(response, 'Failed to load discount history');
  },

  // Upload receipt
  async uploadReceipt(formData) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/storage/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    if (!response.ok) throw new Error('Failed to upload receipt');
    return response.json();
  },

  // Student fee export (backend returns JSON — build CSV in UI)
  async downloadStudentHistory(studentId) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/fees/student/${studentId}/export`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error('Failed to download history');
    return response.json();
  }
};

const FEE_PAGE_SIZE = 10;

export default function FeeManagement() {
  usePageMeta(PAGE_META.fees);
  const currentUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isCounselor = currentUser?.role === 'counselor';
  const [activeTab, setActiveTab] = useState('overview');
  const [recentPayments, setRecentPayments] = useState([]);
  const [upcomingDue, setUpcomingDue] = useState([]);
  const [paymentsPage, setPaymentsPage]   = useState(1);
  const [duePage,      setDuePage]        = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentInvoices, setStudentInvoices] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [studentRefunds, setStudentRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  // Dialog states
  const [isRecordPaymentDialogOpen, setIsRecordPaymentDialogOpen] = useState(false);
  const [quickPaymentDue, setQuickPaymentDue] = useState(null);
  const [isSendReminderDialogOpen, setIsSendReminderDialogOpen] = useState(false);
  const [isViewStudentDialogOpen, setIsViewStudentDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  // Fee adjustment dialog state
  const [isAdjustFeeDialogOpen, setIsAdjustFeeDialogOpen] = useState(false);
  const [feeExportFrom, setFeeExportFrom] = useState('');
  const [feeExportTo, setFeeExportTo] = useState('');
  const [feeExporting, setFeeExporting] = useState(false);
  const [selectedInvoiceForAdjust, setSelectedInvoiceForAdjust] = useState(null);
  const [feeAdjustForm, setFeeAdjustForm] = useState({ newAmount: '', reason: '' });
  const [feePreview, setFeePreview] = useState(null);
  const [adjustLoading, setAdjustLoading] = useState(false);

  // Additional discount dialog state
  const [isReduceFeeDialogOpen, setIsReduceFeeDialogOpen] = useState(false);
  const [selectedInvoiceForDiscount, setSelectedInvoiceForDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({ discountAmount: '', reason: '' });
  const [discountHistory, setDiscountHistory] = useState([]);
  const [discountHistoryLoading, setDiscountHistoryLoading] = useState(false);
  const [discountLoading, setDiscountLoading] = useState(false);

  // Manage-fee dropdown (which invoice has the menu open)
  const [manageFeeOpenId, setManageFeeOpenId] = useState(null);

  // Receipt search states
  const [receiptSearchQuery, setReceiptSearchQuery] = useState('');
  const [receiptSearchLoading, setReceiptSearchLoading] = useState(false);
  const [receiptResult, setReceiptResult] = useState(null);
  const [receiptSearchError, setReceiptSearchError] = useState('');
  const [isReceiptDialogOpen, setIsReceiptDialogOpen] = useState(false);

  // Form states
  const [reminderData, setReminderData] = useState({
    studentId: '',
    invoiceId: '',
    installmentNo: '',
    channels: ['email'],
    customMessage: ''
  });

  // Inject custom styles
  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.textContent = styles;
    document.head.appendChild(styleTag);
    return () => document.head.removeChild(styleTag);
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleExportFeeCollection = async () => {
    try {
      setFeeExporting(true);
      const params = new URLSearchParams();
      params.set('limit', '5000');
      if (feeExportFrom) params.set('startDate', feeExportFrom);
      if (feeExportTo) params.set('endDate', feeExportTo);
      const response = await fetch(`${API_BASE_URL}/fees/export?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const json = await response.json();
      const data = json.data || [];
      const headers = ['Payment Date', 'Student Name', 'Email', 'Phone', 'Course', 'Batch', 'Installment #', 'Amount (₹)', 'Mode', 'Receipt No', 'Payment Status', 'Recorded By'];
      const rows = data.map((p) => [
        formatDateForCsv(p.paymentDate),
        p.studentName  || '-',
        p.studentEmail || '-',
        p.studentPhone || '-',
        p.courseName   || '-',
        p.batchName    || '-',
        p.installmentNo ?? '-',
        p.amount       || 0,
        p.mode         || '-',
        p.receiptNo    || '-',
        p.status       || '-',
        p.recordedBy   || '-',
      ]);
      downloadCsv(headers, rows, `fee-collection-${feeExportFrom || 'all'}-${feeExportTo || 'all'}.csv`);
      toast.success(`Exported ${rows.length} fee records`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to export fee collection');
    } finally {
      setFeeExporting(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Simulate minimum loading time for smooth animation display
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 1500));
      
      const [recentData, dueData] = await Promise.all([
        feeApiService.getRecentlyPaid(),
        feeApiService.getUpcomingDueDates(),
        minLoadTime
      ]);

      setRecentPayments(recentData.recentPayments || []);
      setUpcomingDue(dueData.upcomingDue || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handleViewStudent = async (student) => {
    try {
      setDialogLoading(true);
      setSelectedStudent(student);
      setIsViewStudentDialogOpen(true);
      // console.log('Fetching data for student:', student._id);

      // Simulate minimum loading time
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 1000));

      const [invoicesData, historyData, refundsData] = await Promise.all([
        feeApiService.getStudentInvoices(student._id),
        feeApiService.getPaymentHistory(student._id),
        fetch(`/student-discontinue/refunds/${student._id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()).catch(() => ({ data: [] })),
        minLoadTime
      ]);

      // console.log('Invoices data:', invoicesData);
      // console.log('History data:', historyData);

      setStudentInvoices(invoicesData.invoices || []);
      setPaymentHistory(historyData.payments || []);
      setStudentRefunds(refundsData.data || []);
    } catch (error) {
      console.error('Error fetching student data:', error);
      toast.error('Failed to load student details');
      // Reset states on error
      setStudentInvoices([]);
      setPaymentHistory([]);
      setStudentRefunds([]);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSendReminder = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await feeApiService.sendFeeReminder(reminderData);

      // Reset form
      setReminderData({
        studentId: '',
        invoiceId: '',
        installmentNo: '',
        channels: ['email'],
        customMessage: ''
      });
      setIsSendReminderDialogOpen(false);

      toast.success('Reminder sent successfully!');
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadHistory = async (studentId) => {
    try {
      setLoading(true);
      const data = await feeApiService.downloadStudentHistory(studentId);
      const payments = data?.payments || [];
      const headers = ['Receipt No', 'Date', 'Course', 'Batch', 'Installment #', 'Amount (₹)', 'Mode', 'Status', 'Recorded By'];
      const rows = payments.map((p) => [
        p.receiptNo ?? '-',
        p.date ? formatDateForCsv(p.date) : '-',
        p.courseName || p.course?.name || '-',
        p.batchName  || p.batch?.name  || '-',
        p.installmentNo ?? '-',
        p.amount ?? 0,
        p.mode ?? '-',
        p.status ?? '-',
        p.recordedBy ?? '-',
      ]);
      downloadCsv(headers, rows, `student-fee-payments-${studentId}.csv`);
      toast.success(
        payments.length
          ? `Exported ${payments.length} payment row(s)`
          : 'Exported (no payments in file)'
      );
    } catch (error) {
      console.error('Error downloading history:', error);
      toast.error('Failed to download history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudentFromRecords = async (student) => {
    try {
      setDialogLoading(true);
      setSelectedStudent(student);
      setIsViewStudentDialogOpen(true);
      // console.log('Student data received from records:', student);

      // Check if student has _id
      if (!student._id) {
        toast.error('Student ID is missing');
        return;
      }

      // Simulate minimum loading time
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 1000));

      const [invoicesData, historyData, refundsData] = await Promise.all([
        feeApiService.getStudentInvoices(student._id),
        feeApiService.getPaymentHistory(student._id),
        fetch(`/student-discontinue/refunds/${student._id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()).catch(() => ({ data: [] })),
        minLoadTime
      ]);

      // console.log('Invoices data from records:', invoicesData);
      // console.log('History data from records:', historyData);

      setStudentInvoices(invoicesData.invoices || []);
      setPaymentHistory(historyData.payments || []);
      setStudentRefunds(refundsData.data || []);
    } catch (error) {
      console.error('Error fetching student data from records:', error);
      toast.error('Failed to load student details');
      setStudentInvoices([]);
      setPaymentHistory([]);
      setStudentRefunds([]);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleReceiptSearch = async (e) => {
    e?.preventDefault();
    if (!receiptSearchQuery.trim()) {
      setReceiptSearchError('Please enter a receipt number');
      return;
    }
    try {
      setReceiptSearchLoading(true);
      setReceiptSearchError('');
      setReceiptResult(null);
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/fees/search-receipt?receiptNo=${encodeURIComponent(receiptSearchQuery.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!response.ok) {
        setReceiptSearchError(data.error || 'Receipt not found');
        return;
      }
      setReceiptResult(data.payment);
      setIsReceiptDialogOpen(true);
    } catch (err) {
      setReceiptSearchError('Failed to search. Please try again.');
    } finally {
      setReceiptSearchLoading(false);
    }
  };

  const handleDownloadReceiptPdf = async (receiptNo) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/fees/download-receipt/${encodeURIComponent(receiptNo)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        toast.error('Failed to download receipt. Please try again.');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${receiptNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download receipt. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentModeColor = (mode) => {
    switch (mode?.toLowerCase()) {
      case 'cash': return 'bg-green-100 text-green-800';
      case 'upi': return 'bg-blue-100 text-blue-800';
      case 'card': return 'bg-purple-100 text-purple-800';
      case 'transfer': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Helper function to check if due date is overdue
  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  /** Same width for Overdue / Pending so columns stay aligned */
  const feeDueStatusClass = (overdue) =>
    cn(
      'fee-status-badge',
      overdue ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
    );

  // Quick actions for reminders
  const handleQuickReminder = (student, invoice, installment) => {
    setReminderData({
      studentId: student._id,
      invoiceId: invoice._id,
      installmentNo: installment.installmentNo,
      channels: ['email', 'whatsapp'],
      customMessage: `Friendly reminder: Your installment ${installment.installmentNo} of ${formatCurrency(installment.amount)} for ${invoice.courseId?.name} is due on ${new Date(installment.dueDate).toLocaleDateString()}. Please make the payment at your earliest convenience.`
    });
    setIsSendReminderDialogOpen(true);
  };

  const handlePaymentRecorded = (payment) => {
    // Refresh data after payment is recorded
    fetchDashboardData();
  };

  const handleQuickPayment = (due) => {
    setQuickPaymentDue(due);
    setIsRecordPaymentDialogOpen(true);
  };

  const openAdjustFeeDialog = (invoice) => {
    setSelectedInvoiceForAdjust(invoice);
    setFeeAdjustForm({ newAmount: invoice.totalAmount || '', reason: '' });
    setFeePreview(null);
    setIsAdjustFeeDialogOpen(true);
  };

  const openReduceFeeDialog = async (invoice) => {
    setSelectedInvoiceForDiscount(invoice);
    setDiscountForm({ discountAmount: '', reason: '' });
    setIsReduceFeeDialogOpen(true);
    // Load discount history
    try {
      setDiscountHistoryLoading(true);
      const data = await feeApiService.getDiscountHistory(invoice._id);
      setDiscountHistory(data.discountHistory || []);
    } catch {
      setDiscountHistory(invoice.discountHistory || []);
    } finally {
      setDiscountHistoryLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    const inv = selectedInvoiceForDiscount;
    if (!inv) return;
    const amt = parseFloat(discountForm.discountAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid discount amount'); return; }
    if (!discountForm.reason.trim()) { toast.error('Reason is required'); return; }

    const collectedAmount = (inv.installments || [])
      .filter(i => i.status !== 'pending')
      .reduce((s, i) => s + (i.paidAmount || 0), 0);
    const newTotal = (inv.totalAmount || 0) - amt;
    if (newTotal < collectedAmount) {
      toast.error(`Discount would reduce total below already-collected amount (₹${collectedAmount.toLocaleString('en-IN')})`);
      return;
    }
    if (newTotal < 0) { toast.error('Discount exceeds current total fee'); return; }

    try {
      setDiscountLoading(true);
      await feeApiService.applyAdditionalDiscount(inv._id, amt, discountForm.reason.trim());
      toast.success('Discount applied successfully');
      setIsReduceFeeDialogOpen(false);
      setSelectedInvoiceForDiscount(null);
      // Refresh student invoices
      if (selectedStudent?._id) {
        const [invoicesData, historyData] = await Promise.all([
          feeApiService.getStudentInvoices(selectedStudent._id),
          feeApiService.getPaymentHistory(selectedStudent._id),
        ]);
        setStudentInvoices(invoicesData.invoices || []);
        setPaymentHistory(historyData.payments || []);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to apply discount'));
    } finally {
      setDiscountLoading(false);
    }
  };

  const handlePreviewAdjust = async (e) => {
    e.preventDefault();
    if (!selectedInvoiceForAdjust || !feeAdjustForm.newAmount) return;
    try {
      setAdjustLoading(true);
      const preview = await feeApiService.previewFeeUpdate(
        selectedInvoiceForAdjust._id,
        Number(feeAdjustForm.newAmount)
      );
      setFeePreview(preview);
    } catch (error) {
      console.error('Error previewing fee update:', error);
      toast.error(getApiErrorMessage(error, 'Failed to preview fee update'));
    } finally {
      setAdjustLoading(false);
    }
  };

  const handleConfirmAdjust = async () => {
    if (!selectedInvoiceForAdjust || !feeAdjustForm.newAmount || !feeAdjustForm.reason) {
      toast.error('Please enter new amount and reason');
      return;
    }
    try {
      setAdjustLoading(true);
      await feeApiService.updateFeeAmount(
        selectedInvoiceForAdjust._id,
        Number(feeAdjustForm.newAmount),
        feeAdjustForm.reason
      );

      // Refresh invoices and history for current student
      if (selectedStudent?._id) {
        const [invoicesData, historyData, refundsData] = await Promise.all([
          feeApiService.getStudentInvoices(selectedStudent._id),
          feeApiService.getPaymentHistory(selectedStudent._id),
          fetch(`/student-discontinue/refunds/${selectedStudent._id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()).catch(() => ({ data: [] }))
        ]);
        setStudentInvoices(invoicesData.invoices || []);
        setPaymentHistory(historyData.payments || []);
        setStudentRefunds(refundsData.data || []);
      }

      setIsAdjustFeeDialogOpen(false);
      setSelectedInvoiceForAdjust(null);
      setFeePreview(null);
      toast.success('Fee updated successfully');
    } catch (error) {
      console.error('Error updating fee amount:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update fee amount'));
    } finally {
      setAdjustLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className={initialLoad ? 'animate-fade-in-up' : ''}>
          <h1 className="text-2xl font-bold text-gray-900">Fee Management</h1>
          <p className="text-gray-600">Manage student fees, payments, and reminders</p>
        </div>

        <div className={`flex flex-wrap gap-2 items-center ${initialLoad ? 'animate-fade-in-up' : ''}`} style={{ animationDelay: '200ms' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={feeExportFrom} onChange={(e) => setFeeExportFrom(e.target.value)} className="border rounded px-2 py-1.5 text-sm w-36" />
            <input type="date" value={feeExportTo} onChange={(e) => setFeeExportTo(e.target.value)} className="border rounded px-2 py-1.5 text-sm w-36" />
            <Button variant="outline" size="sm" onClick={handleExportFeeCollection} disabled={feeExporting}>
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              {feeExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
          <Button
            onClick={() => setIsRecordPaymentDialogOpen(true)}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:shadow-lg transition-all duration-300"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Record Payment
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-2">
          <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
          <TabsTrigger value="recent" className="text-xs sm:text-sm">Recently Paid</TabsTrigger>
          <TabsTrigger value="due" className="text-xs sm:text-sm">Due Reminders</TabsTrigger>
          <TabsTrigger value="students" className="text-xs sm:text-sm">All Students</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">

          {/* ── Receipt Search Bar ── */}
          <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 animate-fade-in-up">
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-1.5">
                <MagnifyingGlassIcon className="h-4 w-4" />
                Search by Receipt Number
              </p>
              <form onSubmit={handleReceiptSearch} className="flex gap-2">
                <Input
                  value={receiptSearchQuery}
                  onChange={(e) => { setReceiptSearchQuery(e.target.value); setReceiptSearchError(''); }}
                  placeholder="Enter receipt no. e.g. RCP-2024-0042"
                  className="bg-white border-green-300 focus:border-green-500 flex-1"
                />
                <Button
                  type="submit"
                  disabled={receiptSearchLoading}
                  className="bg-green-600 hover:bg-green-700 text-white shrink-0"
                >
                  {receiptSearchLoading
                    ? <span className="flex items-center gap-1"><span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Searching…</span>
                    : <><MagnifyingGlassIcon className="h-4 w-4 mr-1" />Search</>
                  }
                </Button>
              </form>
              {receiptSearchError && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <ExclamationTriangleIcon className="h-4 w-4" />{receiptSearchError}
                </p>
              )}
            </CardContent>
          </Card>

          {loading ? (
            <OverviewSkeleton />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {!isCounselor && (
                  <Card className="card-hover animate-fade-in-up" style={{ animationDelay: '0ms' }}>
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total Collected</p>
                          <p className="text-xl sm:text-2xl font-bold text-gray-900">
                            {formatCurrency(recentPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0))}
                          </p>
                        </div>
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CurrencyRupeeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {!isCounselor && (
                  <Card className="card-hover animate-fade-in-up" style={{ animationDelay: '100ms' }}>
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Pending Payments</p>
                          <p className="text-xl sm:text-2xl font-bold text-gray-900">{upcomingDue.length}</p>
                        </div>
                        <div className="p-2 bg-yellow-100 rounded-lg">
                          <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="card-hover animate-fade-in-up" style={{ animationDelay: '200ms' }}>
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Overdue</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">
                          {upcomingDue.filter(due => isOverdue(due.dueDate)).length}
                        </p>
                      </div>
                      <div className="p-2 bg-red-100 rounded-lg">
                        <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-hover animate-fade-in-up" style={{ animationDelay: '300ms' }}>
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Recent Payments</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">{recentPayments.length}</p>
                      </div>
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Recently Paid Section */}
                <Card className="animate-fade-in-up" style={{ animationDelay: '400ms' }}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg sm:text-xl">Recently Paid</CardTitle>
                    <CardDescription className="text-sm">Latest fee payments received</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {recentPayments.slice(0, 5).map((payment, index) => (
                        <div 
                          key={payment._id} 
                          className="flex items-center justify-between p-3 border rounded-lg hover:shadow-md transition-all duration-300 animate-fade-in-up"
                          style={{ animationDelay: `${500 + index * 100}ms` }}
                        >
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{payment.studentName}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {payment.courseName} • Installment {payment.installmentNo}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className="font-bold text-green-600 text-sm sm:text-base">{formatCurrency(payment.amount)}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(payment.paymentDate).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                      {recentPayments.length === 0 && (
                        <div className="text-center py-8 text-gray-500 animate-fade-in">
                          <CurrencyRupeeIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm">No recent payments found</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Due Reminders Section */}
                <Card className="animate-fade-in-up" style={{ animationDelay: '500ms' }}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg sm:text-xl">Upcoming Due Dates</CardTitle>
                    <CardDescription className="text-sm">Students with pending payments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {upcomingDue.slice(0, 5).map((due, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-3 border rounded-lg hover:shadow-md transition-all duration-300 animate-fade-in-up"
                          style={{ animationDelay: `${600 + index * 100}ms` }}
                        >
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isOverdue(due.dueDate) ? 'bg-red-100' : 'bg-yellow-100'
                              }`}>
                              <CalendarDaysIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${isOverdue(due.dueDate) ? 'text-red-600' : 'text-yellow-600'
                                }`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{due.studentName}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {due.courseName} • Installment {due.installmentNo}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className="font-bold text-sm sm:text-base">{formatCurrency(due.amount)}</p>
                            <p className={`text-xs ${isOverdue(due.dueDate) ? 'text-red-600' : 'text-gray-500'
                              }`}>
                              Due: {new Date(due.dueDate).toLocaleDateString()}
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-1 h-6 text-xs hover:shadow-md transition-all duration-300"
                              onClick={() => handleQuickReminder(
                                { _id: due.studentId, name: due.studentName },
                                { _id: due.invoiceId, courseId: { name: due.courseName } },
                                { installmentNo: due.installmentNo, amount: due.amount, dueDate: due.dueDate }
                              )}
                            >
                              <EnvelopeIcon className="h-3 w-3 mr-1" />
                              Remind
                            </Button>
                          </div>
                        </div>
                      ))}
                      {upcomingDue.length === 0 && (
                        <div className="text-center py-8 text-gray-500 animate-fade-in">
                          <CheckCircleIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm">No upcoming due payments</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Recently Paid Tab */}
        <TabsContent value="recent">
          {loading ? (
            <TableSkeleton rows={5} columns={7} />
          ) : (
            <Card className="animate-fade-in">
              <CardHeader>
                <CardTitle>Recent Payments</CardTitle>
                <CardDescription>All recent fee payments</CardDescription>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <div className="overflow-x-auto rounded-lg border border-gray-100 bg-white/50">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-white font-semibold">Student</TableHead>
                        <TableHead className="text-white font-semibold">Course</TableHead>
                        <TableHead className="text-white font-semibold">Installment</TableHead>
                        <TableHead className="text-white font-semibold">Amount</TableHead>
                        <TableHead className="text-white font-semibold">Date</TableHead>
                        <TableHead className="text-white font-semibold">Mode</TableHead>
                        <TableHead className="text-center text-white font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentPayments
                        .slice((paymentsPage - 1) * FEE_PAGE_SIZE, paymentsPage * FEE_PAGE_SIZE)
                        .map((payment, index) => (
                        <TableRow 
                          key={payment._id} 
                          className="animate-fade-in-up hover:bg-gray-50 transition-colors"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <TableCell className="px-3 py-3 font-medium text-black">{payment.studentName}</TableCell>
                          <TableCell className="px-3 py-3 text-black">{payment.courseName}</TableCell>
                          <TableCell className="px-3 py-3 text-black">#{payment.installmentNo}</TableCell>
                          <TableCell className="px-3 py-3 font-bold text-green-600">
                            {formatCurrency(payment.amount)}
                          </TableCell>
                          <TableCell className="px-3 py-3 text-gray-700">
                            {new Date(payment.paymentDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="px-3 py-3">
                            <span className={cn('payment-mode-badge', getPaymentModeColor(payment.mode))}>
                              {payment.mode}
                            </span>
                          </TableCell>
                          <TableCell className="px-3 py-3 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewStudent({
                                  _id: payment.studentId,
                                  name: payment.studentName
                                })}
                                className="hover:shadow-md transition-all duration-300"
                                title="View student"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {recentPayments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            <div className="animate-fade-in">
                              <CurrencyRupeeIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                              <p>No recent payments found</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                {/* Pagination */}
                {recentPayments.length > FEE_PAGE_SIZE && (
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500">
                      Showing {(paymentsPage - 1) * FEE_PAGE_SIZE + 1}–{Math.min(paymentsPage * FEE_PAGE_SIZE, recentPayments.length)} of {recentPayments.length}
                    </p>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" disabled={paymentsPage <= 1}
                        onClick={() => setPaymentsPage(p => p - 1)} className="text-xs h-7">← Prev</Button>
                      {Array.from({ length: Math.min(Math.ceil(recentPayments.length / FEE_PAGE_SIZE), 5) }, (_, i) => {
                        const totalPages = Math.ceil(recentPayments.length / FEE_PAGE_SIZE);
                        const p = totalPages <= 5 ? i + 1 : paymentsPage <= 3 ? i + 1 : paymentsPage >= totalPages - 2 ? totalPages - 4 + i : paymentsPage - 2 + i;
                        return (
                          <Button key={p} size="sm" variant={p === paymentsPage ? 'default' : 'outline'}
                            onClick={() => setPaymentsPage(p)}
                            className={`text-xs h-7 w-7 ${p === paymentsPage ? 'bg-green-600 text-white' : ''}`}>{p}</Button>
                        );
                      })}
                      <Button size="sm" variant="outline" disabled={paymentsPage >= Math.ceil(recentPayments.length / FEE_PAGE_SIZE)}
                        onClick={() => setPaymentsPage(p => p + 1)} className="text-xs h-7">Next →</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Due Reminders Tab */}
        <TabsContent value="due">
          {loading ? (
            <TableSkeleton rows={5} columns={7} />
          ) : (
            <Card className="animate-fade-in">
              <CardHeader>
                <CardTitle>Due Payments & Reminders</CardTitle>
                <CardDescription>Manage pending payments and send reminders</CardDescription>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <div className="overflow-x-auto rounded-lg border border-gray-100 bg-white/50">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-white font-semibold">Student</TableHead>
                        <TableHead className="text-white font-semibold">Course</TableHead>
                        <TableHead className="text-white font-semibold">Installment</TableHead>
                        <TableHead className="text-white font-semibold">Amount</TableHead>
                        <TableHead className="text-white font-semibold">Due Date</TableHead>
                        <TableHead className="text-white font-semibold">Status</TableHead>
                        <TableHead className="text-center text-white font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {upcomingDue
                        .slice((duePage - 1) * FEE_PAGE_SIZE, duePage * FEE_PAGE_SIZE)
                        .map((due, index) => (
                        <TableRow 
                          key={index} 
                          className="animate-fade-in-up hover:bg-gray-50 transition-colors"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <TableCell className="px-3 py-3 font-medium text-black">{due.studentName}</TableCell>
                          <TableCell className="px-3 py-3 text-black">{due.courseName}</TableCell>
                          <TableCell className="px-3 py-3 text-black">#{due.installmentNo}</TableCell>
                          <TableCell className="px-3 py-3 font-bold text-black">
                            {formatCurrency(due.amount)}
                          </TableCell>
                          <TableCell className="px-3 py-3">
                            <span
                              className={
                                isOverdue(due.dueDate) ? 'font-semibold text-red-600' : 'text-gray-800'
                              }
                            >
                              {new Date(due.dueDate).toLocaleDateString()}
                            </span>
                          </TableCell>
                          <TableCell className="px-3 py-3">
                            <span className={feeDueStatusClass(isOverdue(due.dueDate))}>
                              {isOverdue(due.dueDate) ? 'Overdue' : 'Pending'}
                            </span>
                          </TableCell>
                          <TableCell className="px-3 py-3 text-center align-middle">
                            <div className="flex flex-wrap items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleQuickReminder(
                                  { _id: due.studentId, name: due.studentName },
                                  { _id: due.invoiceId, courseId: { name: due.courseName } },
                                  { installmentNo: due.installmentNo, amount: due.amount, dueDate: due.dueDate }
                                )}
                                className="hover:shadow-md transition-all duration-300"
                                title="Send reminder"
                              >
                                <EnvelopeIcon className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleQuickPayment(due)}
                                className="hover:shadow-md transition-all duration-300"
                                title="Record payment"
                              >
                                <CurrencyRupeeIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {upcomingDue.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            <div className="animate-fade-in">
                              <CheckCircleIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                              <p>No due payments found</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                {/* Pagination */}
                {upcomingDue.length > FEE_PAGE_SIZE && (() => {
  const totalPages = Math.ceil(upcomingDue.length / FEE_PAGE_SIZE);
  return (
    <div className="flex items-center justify-between mt-3 pt-3 border-t">
      <p className="text-xs text-gray-500">
        Showing {(duePage - 1) * FEE_PAGE_SIZE + 1}–{Math.min(duePage * FEE_PAGE_SIZE, upcomingDue.length)} of {upcomingDue.length}
      </p>
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="outline" disabled={duePage <= 1}
          onClick={() => setDuePage(1)} className="text-xs h-7">« First</Button>
        <Button size="sm" variant="outline" disabled={duePage <= 1}
          onClick={() => setDuePage(p => p - 1)} className="text-xs h-7">← Prev</Button>
        {duePage > 3 && totalPages > 5 && (
          <span className="text-xs text-gray-400 px-1">…</span>
        )}
        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
          const p = totalPages <= 5 ? i + 1 : duePage <= 3 ? i + 1 : duePage >= totalPages - 2 ? totalPages - 4 + i : duePage - 2 + i;
          return (
            <Button key={p} size="sm" variant={p === duePage ? 'default' : 'outline'}
              onClick={() => setDuePage(p)}
              className={`text-xs h-7 w-7 ${p === duePage ? 'bg-amber-600 text-white' : ''}`}>{p}</Button>
          );
        })}
        {duePage < totalPages - 2 && totalPages > 5 && (
          <span className="text-xs text-gray-400 px-1">…</span>
        )}
        <Button size="sm" variant="outline" disabled={duePage >= totalPages}
          onClick={() => setDuePage(p => p + 1)} className="text-xs h-7">Next →</Button>
        <Button size="sm" variant="outline" disabled={duePage >= totalPages}
          onClick={() => setDuePage(totalPages)} className="text-xs h-7">Last »</Button>
      </div>
    </div>
  );
})()}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* All Students Tab */}
        <TabsContent value="students">
          <StudentFeeRecords
            onViewStudentDetails={handleViewStudentFromRecords}
          />
        </TabsContent>
      </Tabs>

      {/* Record Payment Dialog */}
      <RecordPaymentDialog
        isOpen={isRecordPaymentDialogOpen}
        onClose={() => {
          setIsRecordPaymentDialogOpen(false);
          setQuickPaymentDue(null);
        }}
        onPaymentRecorded={handlePaymentRecorded}
        preSelectedDue={quickPaymentDue}
      />

      {/* Adjust Fee Dialog */}
      <Dialog open={isAdjustFeeDialogOpen} onOpenChange={setIsAdjustFeeDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-w-[95vw] max-h-[90vh] flex-col overflow-y-auto overflow-x-hidden animate-fade-in-up">
          {selectedInvoiceForAdjust && (
            <>
              <DialogHeader>
                <DialogTitle>Adjust Fee Amount</DialogTitle>
                <DialogDescription>
                  Customize this student's total course fee. Existing installments will be recalculated.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handlePreviewAdjust} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Current Total</p>
                    <p className="font-semibold">
                      {formatCurrency(selectedInvoiceForAdjust.totalAmount || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Original Total</p>
                    <p className="font-semibold">
                      {selectedInvoiceForAdjust.originalTotalAmount
                        ? formatCurrency(selectedInvoiceForAdjust.originalTotalAmount)
                        : '—'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newAmount">New Total Amount</Label>
                  <Input
                    id="newAmount"
                    type="number"
                    min="0"
                    value={feeAdjustForm.newAmount}
                    onChange={(e) =>
                      setFeeAdjustForm({
                        ...feeAdjustForm,
                        newAmount: e.target.value
                      })
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason for Adjustment</Label>
                  <Textarea
                    id="reason"
                    rows={3}
                    value={feeAdjustForm.reason}
                    onChange={(e) =>
                      setFeeAdjustForm({
                        ...feeAdjustForm,
                        reason: e.target.value
                      })
                    }
                    placeholder="E.g., special discount, scholarship, negotiation, etc."
                    required
                  />
                </div>

                {feePreview && (
                  <div className="border rounded-lg p-3 bg-gray-50 space-y-2 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Original Total</span>
                      <span className="font-semibold">
                        {formatCurrency(feePreview.originalTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">New Total</span>
                      <span className="font-semibold text-emerald-700">
                        {formatCurrency(feePreview.newTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Discount</span>
                      <span className="font-semibold text-emerald-700">
                        {formatCurrency(feePreview.discountAmount)}
                      </span>
                    </div>
                    <div className="mt-2">
                      <p className="text-gray-600 mb-1">Installment breakdown</p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {feePreview.installments?.map((inst) => (
                          <div key={inst.installmentNo} className="flex justify-between">
                            <span>
                              #{inst.installmentNo}{' '}
                              {new Date(inst.dueDate).toLocaleDateString()}
                            </span>
                            <span>
                              {formatCurrency(inst.newAmount)}{' '}
                              <span className="text-xs text-gray-500 line-through ml-1">
                                {formatCurrency(inst.originalAmount)}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <DialogFooter className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsAdjustFeeDialogOpen(false);
                      setSelectedInvoiceForAdjust(null);
                      setFeePreview(null);
                    }}
                    className="w-full sm:w-auto order-2 sm:order-1"
                    disabled={adjustLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="outline"
                    className="w-full sm:w-auto order-1 sm:order-2"
                    disabled={adjustLoading}
                  >
                    {adjustLoading ? 'Calculating...' : 'Preview Changes'}
                  </Button>
                  <Button
                    type="button"
                    className="w-full sm:w-auto order-3"
                    onClick={handleConfirmAdjust}
                    disabled={adjustLoading}
                  >
                    {adjustLoading ? 'Updating...' : 'Confirm Update'}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Reduce Fee / Additional Discount Dialog ───────────────────────── */}
      <Dialog open={isReduceFeeDialogOpen} onOpenChange={(o) => { if (!discountLoading) setIsReduceFeeDialogOpen(o); }}>
        <DialogContent className="sm:max-w-[520px] max-w-[95vw] p-0 overflow-hidden rounded-2xl max-h-[92vh] flex flex-col">
          {selectedInvoiceForDiscount && (() => {
            const inv       = selectedInvoiceForDiscount;
            const collected = (inv.installments || [])
              .filter(i => i.status !== 'pending')
              .reduce((s, i) => s + (i.paidAmount || 0), 0);
            const pendingAmt = (inv.installments || [])
              .filter(i => i.status === 'pending')
              .reduce((s, i) => s + (i.remainingAmount || i.amount || 0), 0);
            const discAmt   = parseFloat(discountForm.discountAmount) || 0;
            const newTotal  = (inv.totalAmount || 0) - discAmt;
            const newPending = Math.max(0, pendingAmt - discAmt);
            const isValid   = discAmt > 0 && newTotal >= collected && !!discountForm.reason.trim();
            return (
              <>
                {/* Gradient header */}
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <ReceiptPercentIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-white">Reduce Fee</h2>
                      <p className="text-xs text-emerald-100 mt-0.5">
                        {inv.courseId?.name || 'Course'} — {inv.studentId?.name || 'Student'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
                  {/* Fee summary cards */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Current Total',  value: inv.totalAmount,      color: 'bg-gray-50 border-gray-200 text-gray-700' },
                      { label: 'Already Paid',   value: collected,             color: 'bg-blue-50 border-blue-200 text-blue-700' },
                      { label: 'Pending',        value: pendingAmt,            color: 'bg-amber-50 border-amber-200 text-amber-700' },
                    ].map(({ label, value, color }) => (
                      <div key={label} className={`rounded-xl border p-3 text-center ${color}`}>
                        <p className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</p>
                        <p className="text-base font-bold mt-0.5">{formatCurrency(value)}</p>
                      </div>
                    ))}
                  </div>

                  {/* Discount amount input */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-700">Discount Amount (₹) <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">₹</span>
                      <Input
                        type="number"
                        min="1"
                        max={pendingAmt}
                        step="0.01"
                        placeholder="Enter discount amount"
                        value={discountForm.discountAmount}
                        onChange={(e) => setDiscountForm(f => ({ ...f, discountAmount: e.target.value }))}
                        className="pl-8 h-10"
                      />
                    </div>
                    {discAmt > 0 && (
                      <div className="flex items-center justify-between text-xs mt-1 px-1">
                        <span className="text-gray-500">New Total After Discount</span>
                        <span className={`font-bold ${newTotal < collected ? 'text-red-600' : 'text-emerald-700'}`}>
                          {formatCurrency(Math.max(0, newTotal))}
                          {newTotal < collected && <span className="ml-1 text-red-500">(below collected!)</span>}
                        </span>
                      </div>
                    )}
                    {discAmt > 0 && newTotal >= collected && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs text-emerald-800 space-y-0.5">
                        <div className="flex justify-between"><span>Discount Given</span><span className="font-semibold">−{formatCurrency(discAmt)}</span></div>
                        <div className="flex justify-between"><span>New Pending (approx.)</span><span className="font-semibold">{formatCurrency(newPending)}</span></div>
                      </div>
                    )}
                  </div>

                  {/* Reason */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-700">Reason for Discount <span className="text-red-500">*</span></Label>
                    <textarea
                      rows={3}
                      placeholder="E.g., scholarship, special case, financial hardship, repeat student, negotiation…"
                      value={discountForm.reason}
                      onChange={(e) => setDiscountForm(f => ({ ...f, reason: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
                    />
                  </div>

                  {/* Discount history */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <TagIcon className="h-3.5 w-3.5" /> Discount History
                    </p>
                    {discountHistoryLoading ? (
                      <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-emerald-500 animate-spin" />
                        Loading history…
                      </div>
                    ) : discountHistory.length === 0 ? (
                      <p className="text-xs text-gray-400 italic py-1">No previous discounts on this invoice.</p>
                    ) : (
                      <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                        {discountHistory.map((h, i) => (
                          <div key={i} className="border border-emerald-100 bg-emerald-50/60 rounded-lg p-3 text-xs space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-emerald-800">−{formatCurrency(h.discountAmount)}</span>
                              <span className="text-gray-500">{new Date(h.appliedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-500">
                              <span className="line-through">{formatCurrency(h.previousTotal)}</span>
                              <span>→</span>
                              <span className="text-emerald-700 font-medium">{formatCurrency(h.newTotal)}</span>
                            </div>
                            {h.appliedBy && (
                              <div className="flex items-center gap-1 text-gray-500">
                                <UserCircleIcon className="h-3.5 w-3.5" />
                                <span>By {h.appliedBy.name || h.appliedBy.email || 'Admin'}</span>
                              </div>
                            )}
                            <p className="text-gray-600 italic">{h.reason}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/80 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsReduceFeeDialogOpen(false)}
                    disabled={discountLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                    onClick={handleApplyDiscount}
                    disabled={!isValid || discountLoading}
                  >
                    {discountLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        Applying…
                      </span>
                    ) : 'Apply Discount'}
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Send Reminder Dialog */}
      <Dialog open={isSendReminderDialogOpen} onOpenChange={setIsSendReminderDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-w-[95vw] animate-fade-in-up">
          <DialogHeader>
            <DialogTitle>Send Fee Reminder</DialogTitle>
            <DialogDescription>Send reminder to student about pending payment</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendReminder}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Channels</Label>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="email-channel"
                      checked={reminderData.channels.includes('email')}
                      onChange={(e) => {
                        const channels = e.target.checked
                          ? [...reminderData.channels, 'email']
                          : reminderData.channels.filter(ch => ch !== 'email');
                        setReminderData({ ...reminderData, channels });
                      }}
                    />
                    <Label htmlFor="email-channel" className="flex items-center text-sm">
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      Email
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="whatsapp-channel"
                      checked={reminderData.channels.includes('whatsapp')}
                      onChange={(e) => {
                        const channels = e.target.checked
                          ? [...reminderData.channels, 'whatsapp']
                          : reminderData.channels.filter(ch => ch !== 'whatsapp');
                        setReminderData({ ...reminderData, channels });
                      }}
                    />
                    <Label htmlFor="whatsapp-channel" className="flex items-center text-sm">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                      WhatsApp
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customMessage">Message</Label>
                <Textarea
                  id="customMessage"
                  value={reminderData.customMessage}
                  onChange={(e) => setReminderData({ ...reminderData, customMessage: e.target.value })}
                  placeholder="Custom reminder message..."
                  rows={4}
                  required
                  className="text-sm resize-none"
                />
              </div>
            </div>
            <DialogFooter className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsSendReminderDialogOpen(false)}
                className="w-full sm:w-auto order-2 sm:order-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="w-full sm:w-auto order-1 sm:order-2"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reminder'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Student Details Dialog */}
      <Dialog open={isViewStudentDialogOpen} onOpenChange={setIsViewStudentDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-w-[95vw] max-h-[90vh] flex-col overflow-y-auto overflow-x-hidden animate-fade-in-up">
          {selectedStudent ? (
            <>
              <DialogHeader>
                <DialogTitle>Student Details: {selectedStudent.name}</DialogTitle>
                <DialogDescription>
                  Complete fee history and payment details
                </DialogDescription>
              </DialogHeader>

              {dialogLoading ? (
                <DialogContentSkeleton />
              ) : (
                <Tabs defaultValue="invoices" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="invoices">Fee Invoices</TabsTrigger>
                    <TabsTrigger value="payments">Payment History</TabsTrigger>
                    <TabsTrigger value="refunds" className="relative">
                      Refunds
                      {studentRefunds.length > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold">
                          {studentRefunds.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="summary">Summary</TabsTrigger>
                  </TabsList>

                  <TabsContent value="invoices" className="space-y-4">
                    {studentInvoices && studentInvoices.length > 0 ? (
                      studentInvoices.map((invoice, index) => {
                        const isTransferred = invoice.status === 'transferred';
                        return (
                        <Card 
                          key={invoice._id} 
                          className={`animate-fade-in-up ${isTransferred ? 'opacity-70 border-dashed border-gray-300' : ''}`}
                          style={{ animationDelay: `${index * 100}ms` }}
                        >
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row justify-between items-start mb-3 gap-2">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className={`font-semibold ${isTransferred ? 'text-gray-400' : ''}`}>
                                    {invoice.courseId?.name}
                                  </h4>
                                  {isTransferred && (
                                    <span className="text-xs text-gray-400 font-normal italic">Past enrollment</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">Batch: {invoice.batchId?.name}</p>
                              </div>
                              <Badge className={isTransferred
                                ? 'bg-amber-100 text-amber-700 border border-amber-300'
                                : getStatusColor(invoice.status)
                              }>
                                {isTransferred ? 'Transferred' : invoice.status}
                              </Badge>
                              {!isTransferred && (
                                <div className="ml-auto mt-2 sm:mt-0 relative">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-violet-300 text-violet-700 hover:bg-violet-50 gap-1.5"
                                    onClick={() => setManageFeeOpenId(manageFeeOpenId === invoice._id ? null : invoice._id)}
                                  >
                                    <CurrencyRupeeIcon className="h-4 w-4" />
                                    Manage Fee
                                    <svg className="h-3 w-3 ml-0.5" viewBox="0 0 12 12" fill="currentColor"><path d="M6 8L1 3h10z"/></svg>
                                  </Button>

                                  {manageFeeOpenId === invoice._id && (
                                    <div
                                      className="absolute right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden w-52"
                                      onMouseLeave={() => setManageFeeOpenId(null)}
                                    >
                                      <button
                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-violet-50 hover:text-violet-700 transition-colors text-left"
                                        onClick={() => { setManageFeeOpenId(null); openAdjustFeeDialog(invoice); }}
                                      >
                                        <CurrencyRupeeIcon className="h-4 w-4 flex-shrink-0 text-violet-500" />
                                        <div>
                                          <p className="font-medium">Adjust Amount</p>
                                          <p className="text-xs text-gray-400">Set a new total fee</p>
                                        </div>
                                      </button>
                                      <div className="border-t border-gray-100 mx-3" />
                                      <button
                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                                        onClick={() => { setManageFeeOpenId(null); openReduceFeeDialog(invoice); }}
                                      >
                                        <TagIcon className="h-4 w-4 flex-shrink-0 text-emerald-500" />
                                        <div>
                                          <p className="font-medium">Apply Discount</p>
                                          <p className="text-xs text-gray-400">Reduce by a discount amount</p>
                                        </div>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-3">
                              <div>
                                <p className="text-gray-600">Total Amount</p>
                                <p className="font-semibold">{formatCurrency(invoice.totalAmount)}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Created</p>
                                <p>{new Date(invoice.createdAt).toLocaleDateString()}</p>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label>Installments:</Label>
                              {invoice.installments.map((installment, idx) => (
                                <div 
                                  key={installment._id} 
                                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-2 border rounded gap-2 animate-fade-in-up"
                                  style={{ animationDelay: `${(index + idx) * 100}ms` }}
                                >
                                  <div>
                                    <p className="font-medium">Installment #{installment.installmentNo}</p>
                                    <p className="text-sm text-gray-600">
                                      Due: {new Date(installment.dueDate).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-semibold">{formatCurrency(installment.amount)}</p>
                                    <Badge className={getStatusColor(installment.status)}>
                                      {installment.status}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-gray-500 animate-fade-in">
                        <DocumentIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">No invoices found for this student</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="payments" className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Button
                        size="sm"
                        onClick={() => handleDownloadHistory(selectedStudent._id)}
                        disabled={!paymentHistory || paymentHistory.length === 0}
                        className="hover:shadow-md transition-all duration-300"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                        Download History
                      </Button>
                    </div>

                    <div className="border rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-white font-semibold">Receipt No</TableHead>
                            <TableHead className="text-white font-semibold">Amount</TableHead>
                            <TableHead className="text-white font-semibold">Installment</TableHead>
                            <TableHead className="text-white font-semibold">Date</TableHead>
                            <TableHead className="text-white font-semibold">Mode</TableHead>
                            <TableHead className="text-white font-semibold">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paymentHistory && paymentHistory.length > 0 ? (
                            paymentHistory.map((payment, index) => (
                              <TableRow 
                                key={payment._id} 
                                className="animate-fade-in-up"
                                style={{ animationDelay: `${index * 50}ms` }}
                              >
                                <TableCell className="font-mono text-xs text-black">
                                  {payment.receiptNo}
                                </TableCell>
                                <TableCell className="font-semibold text-green-600">
                                  {formatCurrency(payment.amount)}
                                </TableCell>
                                <TableCell className="text-black">#{payment.installmentNo}</TableCell>
                                <TableCell className="text-black">
                                  {new Date(payment.paymentDate).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  <Badge className={getPaymentModeColor(payment.mode)}>
                                    {payment.mode}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className={getStatusColor(payment.status)}>
                                    {payment.status}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                                <div className="animate-fade-in">
                                  <CurrencyRupeeIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                  <p>No payment history found</p>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  <TabsContent value="refunds" className="space-y-4">
                    {studentRefunds.length > 0 ? (
                      studentRefunds.map((refund, idx) => (
                        <Card key={refund._id} className="border-red-100 animate-fade-in-up" style={{ animationDelay: `${idx * 80}ms` }}>
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-3">
                              <div>
                                <p className="font-semibold text-gray-800">{refund.courseId?.name || '—'}</p>
                                <p className="text-sm text-gray-500">Batch: {refund.batchId?.name || '—'}</p>
                              </div>
                              <Badge className={
                                refund.status === 'processed' ? 'bg-green-100 text-green-700 border border-green-300' :
                                refund.status === 'failed'    ? 'bg-red-100 text-red-700 border border-red-300' :
                                'bg-amber-100 text-amber-700 border border-amber-300'
                              }>
                                {refund.status}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm mb-3">
                              <div>
                                <p className="text-gray-500">Refund Amount</p>
                                <p className="font-bold text-red-600">₹{(refund.amount || 0).toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Refund Type</p>
                                <p className="font-medium capitalize">{(refund.refundType || '').replace('_', ' ')}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Mode</p>
                                <p className="font-medium capitalize">{refund.refundMode || '—'}</p>
                              </div>
                              {refund.referenceNo && (
                                <div>
                                  <p className="text-gray-500">Reference No.</p>
                                  <p className="font-mono text-xs">{refund.referenceNo}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-gray-500">Date</p>
                                <p>{refund.processedAt ? new Date(refund.processedAt).toLocaleDateString() : '—'}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Processed By</p>
                                <p>{refund.processedBy?.name || '—'}</p>
                              </div>
                            </div>

                            {refund.reason && (
                              <div className="bg-gray-50 rounded p-2 text-sm text-gray-600">
                                <span className="font-medium">Reason: </span>{refund.reason}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center py-10 text-gray-400 animate-fade-in">
                        <CurrencyRupeeIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                        <p className="text-sm">No refund records found for this student</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="summary">
                    <div className="space-y-4 animate-fade-in">
                      <Card>
                        <CardContent className="p-4">
                          <h4 className="font-semibold mb-3">Fee Summary</h4>
                          {(() => {
                            // Exclude transferred (historical) invoices from fee totals
                            const activeInvoices = (studentInvoices || []).filter(inv => inv.status !== 'transferred');
                            const activeTotalFee = activeInvoices.reduce((s, inv) => s + (inv.totalAmount || 0), 0);
                            const activePaid = activeInvoices.reduce((s, inv) =>
                              s + inv.installments.reduce((ss, inst) => ss + (inst.paidAmount || 0), 0), 0);
                            const activePending = Math.max(0, activeTotalFee - activePaid);
                            const totalRefunded = studentRefunds.reduce((s, r) => s + (r.amount || 0), 0);
                            return (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600">Total Fee</p>
                              <p className="font-semibold">{formatCurrency(activeTotalFee)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Total Paid</p>
                              <p className="font-semibold text-green-600">{formatCurrency(activePaid)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Pending</p>
                              <p className="font-semibold text-red-600">{formatCurrency(activePending)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Total Payments</p>
                              <p className="font-semibold">{paymentHistory.length}</p>
                            </div>
                            {totalRefunded > 0 && (
                              <div className="sm:col-span-2 bg-red-50 border border-red-200 rounded p-2">
                                <p className="text-red-600">Total Refunded</p>
                                <p className="font-bold text-red-700">{formatCurrency(totalRefunded)}</p>
                                <p className="text-xs text-red-400">{studentRefunds.length} refund transaction{studentRefunds.length > 1 ? 's' : ''}</p>
                              </div>
                            )}
                          </div>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                </Tabs>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsViewStudentDialogOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : (
            <DialogContentSkeleton />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Receipt Search Result Dialog ── */}
      <Dialog open={isReceiptDialogOpen} onOpenChange={(open) => { setIsReceiptDialogOpen(open); if (!open) setReceiptResult(null); }}>
        <DialogContent className="sm:max-w-[540px] max-w-[95vw] max-h-[90vh] overflow-y-auto animate-fade-in-up">
          {receiptResult && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-green-700">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  Payment Receipt Details
                </DialogTitle>
                <DialogDescription>
                  Receipt No: <span className="font-mono font-semibold text-gray-700">{receiptResult.receiptNo}</span>
                </DialogDescription>
              </DialogHeader>

              {/* Status badge + amount hero */}
              <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-1">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Amount Paid</p>
                  <p className="text-2xl font-bold text-green-700">₹{(receiptResult.amount || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {receiptResult.paymentDate
                      ? new Date(receiptResult.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
                      : '—'}
                  </p>
                </div>
                <div className="text-right">
                  <Badge className={getStatusColor(receiptResult.status)}>{receiptResult.status}</Badge>
                  <p className="text-xs mt-1 capitalize text-gray-500">{receiptResult.mode}</p>
                </div>
              </div>

              {/* Student info */}
              <div className="rounded-xl border divide-y text-sm">
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-gray-500">Student</span>
                  <span className="font-semibold">{receiptResult.student?.name || '—'}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-gray-500">Email</span>
                  <span>{receiptResult.student?.email || '—'}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-gray-500">Phone</span>
                  <span>{receiptResult.student?.phone || '—'}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-gray-500">Course</span>
                  <span className="font-medium">{receiptResult.invoice?.courseName || '—'}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-gray-500">Batch</span>
                  <span>{receiptResult.invoice?.batchName || '—'}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-gray-500">Installment</span>
                  <span className="font-medium">#{receiptResult.installmentNo}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-gray-500">Payment Mode</span>
                  <Badge className={getPaymentModeColor(receiptResult.mode)}>{receiptResult.mode}</Badge>
                </div>
                {receiptResult.referenceNo && (
                  <div className="flex justify-between px-4 py-2.5">
                    <span className="text-gray-500">Reference No.</span>
                    <span className="font-mono text-xs">{receiptResult.referenceNo}</span>
                  </div>
                )}
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-gray-500">Recorded By</span>
                  <span>{receiptResult.recordedBy || '—'}</span>
                </div>
                {receiptResult.notes && (
                  <div className="px-4 py-2.5">
                    <span className="text-gray-500">Notes</span>
                    <p className="mt-0.5 text-gray-700">{receiptResult.notes}</p>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setIsReceiptDialogOpen(false)}>
                  <XMarkIcon className="h-4 w-4 mr-1" /> Close
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
                  onClick={() => handleDownloadReceiptPdf(receiptResult.receiptNo)}
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-1" /> Download PDF Receipt
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
